import { HEX_3_PATTERN, HEX_6_PATTERN, normalizeColorHex } from '../../utils/colorHex';

export { DEFAULT_COLOR_HEX, normalizeColorHex } from '../../utils/colorHex';

export function isColorHexDraft(value) {
  return HEX_6_PATTERN.test(value) || HEX_3_PATTERN.test(value);
}

export function clampColorValue(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function toHexChannel(value) {
  return Math.round(clampColorValue(value, 0, 255)).toString(16).padStart(2, '0');
}

export function createColorChangeEvent(value, metadata = {}) {
  const target = {
    value,
    ...(metadata.id ? { id: metadata.id } : {}),
    ...(metadata.name ? { name: metadata.name } : {}),
  };

  return {
    target,
    currentTarget: target,
  };
}

export function hexToRgb(hex) {
  const normalized = normalizeColorHex(hex);
  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
}

export function rgbToHex({ r, g, b }) {
  return `#${toHexChannel(r)}${toHexChannel(g)}${toHexChannel(b)}`;
}

export function rgbToHsv({ r, g, b }) {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;

  if (delta !== 0) {
    if (max === red) {
      hue = ((green - blue) / delta) % 6;
    } else if (max === green) {
      hue = (blue - red) / delta + 2;
    } else {
      hue = (red - green) / delta + 4;
    }
    hue *= 60;
  }

  if (hue < 0) {
    hue += 360;
  }

  return {
    h: hue,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
}

export function hsvToRgb({ h, s, v }) {
  const chroma = v * s;
  const huePrime = h / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));
  const m = v - chroma;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = x;
  } else if (huePrime < 2) {
    red = x;
    green = chroma;
  } else if (huePrime < 3) {
    green = chroma;
    blue = x;
  } else if (huePrime < 4) {
    green = x;
    blue = chroma;
  } else if (huePrime < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  return {
    r: (red + m) * 255,
    g: (green + m) * 255,
    b: (blue + m) * 255,
  };
}

export function getHsvFromHex(value) {
  return rgbToHsv(hexToRgb(normalizeColorHex(value)));
}

export function getColorPickerStyle(value) {
  const hex = normalizeColorHex(value);
  const hsv = getHsvFromHex(hex);

  return {
    '--ui-color-current': hex,
    '--ui-color-picker-hue-color': `hsl(${Math.round(hsv.h)} 100% 50%)`,
    '--ui-color-picker-saturation': `${hsv.s * 100}%`,
    '--ui-color-picker-value': `${(1 - hsv.v) * 100}%`,
  };
}
